import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { billingService } from '@services/billingService';
import toast from 'react-hot-toast';

export const useBilling = () => {
  const queryClient = useQueryClient();

  // Buscar planos disponíveis
  const {
    data: plans = [],
    isLoading: plansLoading,
  } = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: billingService.getPlans,
    staleTime: 1000 * 60 * 60, // 1 hora
  });

  // Buscar status da assinatura
  const {
    data: subscription,
    isLoading: subscriptionLoading,
    refetch: refetchSubscription,
  } = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: billingService.getSubscriptionStatus,
  });

  // Criar checkout
  const checkoutMutation = useMutation({
    mutationFn: ({ plan, interval }) => billingService.createCheckout(plan, interval),
    onSuccess: (data) => {
      // Redirecionar para o Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao criar checkout');
    },
  });

  // Abrir portal do cliente
  const portalMutation = useMutation({
    mutationFn: billingService.createPortalSession,
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao abrir portal');
    },
  });

  // Cancelar assinatura
  const cancelMutation = useMutation({
    mutationFn: billingService.cancelSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      toast.success('Assinatura será cancelada ao final do período');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao cancelar');
    },
  });

  // Reativar assinatura
  const reactivateMutation = useMutation({
    mutationFn: billingService.reactivateSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      toast.success('Assinatura reativada com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao reativar');
    },
  });

  return {
    plans,
    subscription,
    isLoading: plansLoading || subscriptionLoading,
    refetchSubscription,
    createCheckout: checkoutMutation.mutateAsync,
    openPortal: portalMutation.mutateAsync,
    cancelSubscription: cancelMutation.mutateAsync,
    reactivateSubscription: reactivateMutation.mutateAsync,
    isCheckingOut: checkoutMutation.isPending,
    isOpeningPortal: portalMutation.isPending,
    isCanceling: cancelMutation.isPending,
    isReactivating: reactivateMutation.isPending,
  };
};

export default useBilling;
